import moment from "moment";
import { useEffect, useState } from "react";
import {
  fetchFilenameSummary,
  type FilenameSummaryItem,
} from "../../../api/bigQuery/filenameSummary";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";

/** Maximum page size supported by the filename-summary API. */
const FILENAME_SUMMARY_PAGE_SIZE = 1000;

/** Only filenames dumped within this window are shown in the reassign modal. */
const FILENAME_LOOKBACK_DAYS = 7;

/**
 * Returns filenames whose `dumped_at` falls within the last seven days.
 *
 * @param items - Filename summary rows from the BigQuery API
 * @returns Filenames ordered by most recently dumped first
 */
export const filterFilenamesFromLastSevenDays = (
  items: FilenameSummaryItem[],
): string[] => {
  const cutoff = moment().subtract(FILENAME_LOOKBACK_DAYS, "days").startOf("day");

  return items
    .filter((item) => {
      const dumpedAt = moment(item.dumped_at);
      return dumpedAt.isValid() && dumpedAt.isSameOrAfter(cutoff);
    })
    .map((item) => item.filename);
};

/**
 * Loads filename dropdown options from the BigQuery filename-summary API.
 * Only filenames dumped in the last seven days are included.
 *
 * @returns Recent filenames, loading state, and fetch error when present.
 */
export function useFilenameSummaryOptions() {
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadFilenameOptions = async () => {
      setLoading(true);
      setError(null);
      try {
        const orgId = getOrgIdForFetchExternalWorklist();
        const response = await fetchFilenameSummary({
          page: 1,
          page_size: FILENAME_SUMMARY_PAGE_SIZE,
          org_id: orgId,
          order_by: [{ column: "dumped_at", direction: "DESC" }],
        });
        if (!cancelled) {
          if (response.success && response.items) {
            setOptions(filterFilenamesFromLastSevenDays(response.items));
          } else {
            setOptions([]);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setOptions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadFilenameOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  return { options, loading, error };
}
